/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import {
  StyledBundleContainer,
  StyledAddonContainer,
  StyledSectionItemContainer,
  StyledSectionItemMain,
} from './MenuItem.styles';
import { useGetBreakpoint } from '../../../../modules/utils';
import clsx from 'clsx';
import {
  addonItem,
  allergenType,
  dietType,
  itemBundle,
  packageBundle,
  sectionBundle,
  sectionItem,
  sectionDescriptionLimit,
  sectionNameMenuLimit,
} from '../../../../modules/utils/constants';
import DietAllergenIcon from '../DietAllergenIcon/DietAllergenIcon';
import { Allergen, Diet, MenuItemTypes } from './MenuItem.types';
import { Text, Types } from '@marriott/mi-ui-library';

// This component can be used to render Bundle Item or Section Item or Addons
export const MenuDetailsItem = (props: MenuItemTypes) => {
  let portSize = useGetBreakpoint();
  const [isMobileViewPort, setIsMobileViewPort] = useState(portSize === 'mobile' || portSize === 'tablet');
  const hidePrice = props?.isPackageBundle;
  const isPremiumMenu = props?.isPremiumMenu || false;

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks, react-hooks/exhaustive-deps
      portSize = useGetBreakpoint();
      setIsMobileViewPort(portSize === 'mobile' || portSize === 'tablet');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getDietList = (item: any) => {
    if (item?.suitableDiets && item?.suitableDiets?.length > 0) {
      if (isPremiumMenu) {
        const dietList = item?.suitableDiets?.map((obj: any) => obj.name);
        return (
          <Text
            copyText={dietList?.join(', ')}
            fontSize={Types.size.medium}
            element={Types.tags.paragraph}
            customClass={'t-overline-alt-normal mb-0'}
          />
        );
      } else {
        const dietList = item?.suitableDiets?.map((diet: Diet) => {
          return <DietAllergenIcon itemCode={diet.code} itemType={dietType} key={diet.name} />;
        });
        return dietList;
      }
    }
    return '';
  };

  const getAllergenList = (item: any) => {
    if (item?.allergens && item?.allergens?.length > 0) {
      if (isPremiumMenu) {
        const allergenList = item?.allergens?.map((obj: any) => obj.name);
        return (
          <Text
            copyText={allergenList?.join(', ')}
            fontSize={Types.size.medium}
            element={Types.tags.paragraph}
            customClass={'t-overline-alt-normal mb-0'}
          />
        );
      } else {
        const allergenList = item?.allergens?.map((allergen: Allergen) => {
          return <DietAllergenIcon itemCode={allergen.code} itemType={allergenType} key={allergen.name} />;
        });
        return allergenList;
      }
    }
    return '';
  };

  const getBundleItem = () => {
    // const itemName =
    //   props?.item?.name <= sectionNameLimit ? props?.item?.name : props?.item?.name?.slice(0, sectionNameLimit);
    return (
      <StyledBundleContainer
        className={clsx('d-flex justify-content-between align-items-center')}
        key={props?.item?.name}
      >
        {props?.item?.name && (
          <span className={clsx(isMobileViewPort ? 't-label-s' : 't-subtitle-m')}>{props?.item?.name}</span>
        )}
        {props?.item?.priceWithCurrency &&
          (isPremiumMenu ? (
            <div className="text-right">
              <span className="t-subtitle-xl">{props?.item?.priceWithCurrency?.split(/[a-zA-Z]+/)[1]}</span>{' '}
              <span className="t-font-xs">{props?.item?.priceWithCurrency?.split(/\d+/)[0]}</span>
            </div>
          ) : (
            <span className={clsx(isMobileViewPort ? 't-label-xs' : 't-label-s', 'item-price')}>
              {props?.item?.priceWithCurrency}
            </span>
          ))}
      </StyledBundleContainer>
    );
  };

  const getAddonItem = () => {
    // const itemName =
    //   props?.item?.name <= itemBundleNameLimit ? props?.item?.name : props?.item?.name?.slice(0, itemBundleNameLimit);

    return (
      <StyledAddonContainer calories={props?.item?.calories || ''} key={props?.item?.name}>
        <div className="d-flex justify-content-between align-items-baseline">
          <div className="addon-name">
            {props?.item?.name && (
              <span
                className={clsx(
                  isPremiumMenu
                    ? isMobileViewPort
                      ? 't-font-alt-xs'
                      : 't-font-alt-s'
                    : isMobileViewPort
                    ? 't-font-xs'
                    : 't-font-s'
                )}
              >
                {!isPremiumMenu && props?.addOn}
                {props?.item?.name}
              </span>
            )}
            {((props && props?.item?.suitableDiets && props?.item?.suitableDiets?.length > 0) ||
              (props && props?.item?.allergens && props?.item?.allergens?.length > 0)) && (
              <div className={clsx('diet-allergen-codes-info d-flex flex-row flex-wrap', isPremiumMenu ? 'mt-3' : '')}>
                {getDietList(props?.item)}
                {getAllergenList(props?.item)}
              </div>
            )}
          </div>
          {props?.item?.calories && (
            <div className={clsx(isPremiumMenu ? 'd-none' : 't-label-xs')}>
              {props?.item?.caloriesLabel}: {props?.item?.calories}
            </div>
          )}
          {!hidePrice &&
            props?.item?.priceWithCurrency &&
            (isPremiumMenu ? (
              <div className="text-right">
                <span className="t-font-m">+{props?.item?.priceWithCurrency?.split(/[a-zA-Z]+/)[1].trimStart()}</span>{' '}
                <span className="t-font-xs">{props?.item?.priceWithCurrency?.split(/\d+/)[0]}</span>
              </div>
            ) : (
              <span className={clsx(isMobileViewPort ? 't-label-xs' : 't-label-s', 'item-price')}>
                +{props?.item?.priceWithCurrency}
              </span>
            ))}
        </div>
        {props?.item?.calories && (
          <div className={clsx(!isPremiumMenu ? 'd-none' : 't-label-xs')}>
            {props?.item?.caloriesLabel}: {props?.item?.calories}
          </div>
        )}
      </StyledAddonContainer>
    );
  };

  const getSectionItem = () => {
    return (
      <StyledSectionItemMain
        className={clsx(
          'd-flex flex-column ml-1',
          isPremiumMenu ? 'pl-0' : 'px-md-5 px-lg-5 px-xl-5',
          !props?.isSingleGroupContent ? 'pr-md-5 mr-md-3' : 'pr-0'
        )}
      >
        <StyledSectionItemContainer
          className={clsx(!isPremiumMenu && 'd-flex justify-content-between align-items-baseline')}
          calories={props?.item?.calories || ''}
          key={props?.item?.name}
        >
          {isPremiumMenu ? (
            <>
              <div
                className={clsx(
                  !hidePrice &&
                    props?.item?.priceWithCurrency &&
                    isPremiumMenu &&
                    'd-flex justify-content-between align-items-baseline'
                )}
              >
                <div className={clsx('section-item-container text-left', isPremiumMenu ? '' : 'mb-2 pb-1')}>
                  <span className={clsx(`sub-title t-subtitle-xl pl-0'}`)}>
                    {props?.item?.name
                      ? props?.item?.name?.length > sectionNameMenuLimit
                        ? props?.item?.name?.slice(0, sectionNameMenuLimit) + '...'
                        : props?.item?.name
                      : null}
                  </span>
                  {/* <div className="diet-allergen-codes-info d-flex flex-row flex-wrap mt-3">
                    {getDietList(props?.item)}
                    {getAllergenList(props?.item)}
                  </div> */}
                </div>
                {props?.item?.calories && (
                  <span className={clsx(isPremiumMenu ? 'd-none' : 't-label-xs')}>
                    {props?.item?.caloriesLabel}: {props?.item?.calories}
                  </span>
                )}
                {!hidePrice &&
                  props?.item?.priceWithCurrency &&
                  (isPremiumMenu ? (
                    <div className="text-right">
                      <span className="t-subtitle-xl">{props?.item?.priceWithCurrency?.split(/[a-zA-Z]+/)[1]}</span>{' '}
                      <span className="t-font-xs">{props?.item?.priceWithCurrency?.split(/\d+/)[0]}</span>
                    </div>
                  ) : (
                    <span className={clsx('item-price', isMobileViewPort ? 't-label-xs' : 't-label-s', 'item-price')}>
                      {props?.item?.priceWithCurrency}
                    </span>
                  ))}
              </div>
              {props?.item?.calories && (
                <span className={clsx(!isPremiumMenu ? 'd-none' : 't-label-xs')}>
                  {props?.item?.caloriesLabel}: {props?.item?.calories}
                </span>
              )}
              <div className="col-md-12 pl-0">
                {' '}
                <p className={clsx('mt-1', isPremiumMenu ? 't-font-alt-xs' : 't-font-xs')}>
                  {props?.item?.description
                    ? props?.item?.description?.length > sectionDescriptionLimit
                      ? props?.item?.description?.slice(0, sectionDescriptionLimit) + '...'
                      : props?.item?.description
                    : null}
                </p>{' '}
              </div>

              {((props && props?.item?.suitableDiets && props?.item?.suitableDiets?.length > 0) ||
                (props && props?.item?.allergens && props?.item?.allergens?.length > 0)) && (
                <div
                  className={clsx(
                    'diet-allergen-codes-info d-flex flex-row flex-wrap mt-2',
                    isPremiumMenu ? 'pt-1' : ''
                  )}
                >
                  {getDietList(props?.item)}
                  {getAllergenList(props?.item)}
                </div>
              )}
            </>
          ) : (
            <>
              <div className={clsx('section-item-container text-left')}>
                <span className={clsx(isMobileViewPort ? 't-subtitle-m' : 't-subtitle-l col-md-12 pl-0')}>
                  {props?.item?.name
                    ? props?.item?.name?.length > sectionNameMenuLimit
                      ? props?.item?.name?.slice(0, sectionNameMenuLimit) + '...'
                      : props?.item?.name
                    : null}
                </span>
                <div className="col-md-12 pl-0">
                  {' '}
                  <p className="t-font-xs mb-1 mt-1">
                    {props?.item?.description
                      ? props?.item?.description?.length > sectionDescriptionLimit
                        ? props?.item?.description?.slice(0, sectionDescriptionLimit) + '...'
                        : props?.item?.description
                      : null}
                  </p>{' '}
                </div>
                {((props && props?.item?.suitableDiets && props?.item?.suitableDiets?.length > 0) ||
                  (props && props?.item?.allergens && props?.item?.allergens?.length > 0)) && (
                  <div className="diet-allergen-codes-info d-flex flex-row flex-wrap">
                    {getDietList(props?.item)}
                    {getAllergenList(props?.item)}
                  </div>
                )}
              </div>

              {props?.item?.calories && (
                <span className="t-label-xs">
                  {props?.item?.caloriesLabel}: {props?.item?.calories}
                </span>
              )}
              {!hidePrice &&
                props?.item?.priceWithCurrency &&
                (isPremiumMenu ? (
                  <div className="text-right">
                    <span className="t-subtitle-m">{props?.item?.priceWithCurrency?.split(/[a-zA-Z]+/)[1]}</span>{' '}
                    <span className="t-font-xs">{props?.item?.priceWithCurrency?.split(/\d+/)[0]}</span>
                  </div>
                ) : (
                  <span className={clsx(isMobileViewPort ? 't-label-xs' : 't-label-s', 'item-price')}>
                    {props?.item?.priceWithCurrency}
                  </span>
                ))}
            </>
          )}
        </StyledSectionItemContainer>
        {props?.item?.itemBundles?.pricePoints &&
          props?.item?.itemBundles?.pricePoints?.map((packageItem: any) => (
            <MenuDetailsItem
              item={packageItem}
              itemType={itemBundle}
              key={packageItem?.name}
              isPackageBundle={hidePrice}
              addOn={props?.addOn}
              isPremiumMenu={props?.isPremiumMenu}
            />
          ))}
        {props?.item?.itemBundles?.addons &&
          props?.item?.itemBundles?.addons.map((addon: any) => (
            <MenuDetailsItem
              item={addon}
              itemType={addonItem}
              key={addon?.name}
              isPackageBundle={hidePrice}
              addOn={props?.addOn}
              isPremiumMenu={props?.isPremiumMenu}
            />
          ))}
      </StyledSectionItemMain>
    );
  };

  const getItemBundle = () => {
    // const itemName =
    //   props?.item?.name <= itemBundleNameLimit ? props?.item?.name : props?.item?.name?.slice(0, itemBundleNameLimit);

    return (
      <StyledSectionItemContainer
        // className="d-flex justify-content-between align-items-baseline"
        calories={props?.item?.calories || ''}
        key={props?.item?.name}
      >
        <div className="d-flex justify-content-between align-items-baseline">
          <div className={clsx('section-item-container text-left')}>
            {props?.item?.name && (
              <span className={clsx(isMobileViewPort ? 't-font-xs' : 't-font-s col-md-12 pl-0', 'sub-title')}>
                {props?.item?.name}
              </span>
            )}
            {((props && props?.item?.suitableDiets && props?.item?.suitableDiets?.length > 0) ||
              (props && props?.item?.allergens && props?.item?.allergens?.length > 0)) && (
              <div className={clsx('diet-allergen-codes-info d-flex flex-row flex-wrap', isPremiumMenu ? 'mt-2' : '')}>
                {getDietList(props?.item)}
                {getAllergenList(props?.item)}
              </div>
            )}
          </div>
          {props?.item?.calories && (
            <span className={clsx(isPremiumMenu ? 'd-none' : 't-label-xs mr-1')}>
              {props?.item?.caloriesLabel}: {props?.item?.calories}
            </span>
          )}
          {!hidePrice &&
            props?.item?.priceWithCurrency &&
            (isPremiumMenu ? (
              <div className="text-right">
                <span className="t-subtitle-m">{props?.item?.priceWithCurrency?.split(/[a-zA-Z]+/)[1]}</span>{' '}
                <span className="t-font-xs">{props?.item?.priceWithCurrency?.split(/\d+/)[0]}</span>
              </div>
            ) : (
              <span className={clsx(isMobileViewPort ? 't-label-xs' : 't-label-s', 'item-price')}>
                {props?.item?.priceWithCurrency}
              </span>
            ))}
        </div>
        {props?.item?.calories && (
          <span className={clsx(!isPremiumMenu ? 'd-none' : 't-label-xs mr-1')}>
            {props?.item?.caloriesLabel}: {props?.item?.calories}
          </span>
        )}
      </StyledSectionItemContainer>
    );
  };

  return props?.itemType === packageBundle || props?.itemType === sectionBundle
    ? getBundleItem()
    : props?.itemType === sectionItem
    ? getSectionItem()
    : props?.itemType === addonItem
    ? getAddonItem()
    : props?.itemType === itemBundle
    ? getItemBundle()
    : null;
};

export default MenuDetailsItem;
